import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import { InvoiceDetailsData } from "../InvoiceDetails/InvoiceDetails";
import { testIds } from "./InvoiceSeparationModal.testIds";

export type InvoiceSeparationModalProps = {
  open: boolean;
  shipments: NonNullable<InvoiceDetailsData["shipments"]>;
  onClose: () => void;
  onSubmit: (shipmentIds: string[]) => void;
  isLoading?: boolean;
};

const InvoiceSeparationModal: React.FC<InvoiceSeparationModalProps> = ({
  open,
  shipments,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation(["finance", "common"]);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedShipmentIds([]);
    }
  }, [open]);

  const toggleShipment = (shipmentId: string) => {
    setSelectedShipmentIds((selected) =>
      selected.includes(shipmentId)
        ? selected.filter((id) => id !== shipmentId)
        : [...selected, shipmentId]
    );
  };

  const allSelected =
    shipments.length > 0 && selectedShipmentIds.length === shipments.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("separateOrders", "Separate orders")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t(
            "separateOrdersDescription",
            "Pick the orders to bill on their own. Each one gets its own invoice."
          )}
        </DialogContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  inputProps={
                    {
                      "data-testid":
                        testIds.invoiceSeparationModalSelectAll,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={allSelected}
                  indeterminate={!allSelected && selectedShipmentIds.length > 0}
                  onChange={() =>
                    setSelectedShipmentIds(
                      allSelected ? [] : shipments.map((s) => s._id)
                    )
                  }
                />
              </TableCell>
              <TableCell>{t("orderNumber", "Order Number")}</TableCell>
              <TableCell>{t("deliveryDate", "Delivery Date")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipments.map((shipment) => {
              const deliveryDate =
                shipment.route?.lastDropoffTime ||
                shipment.trip?.lastDropoffTime;
              return (
                <TableRow
                  key={shipment._id}
                  hover
                  onClick={() => toggleShipment(shipment._id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell
                    padding="checkbox"
                    // The row toggles too, without this a click on the box counts twice
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      inputProps={
                        {
                          "data-testid":
                            testIds.invoiceSeparationModalOrder(
                              shipment._id
                            ),
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                      checked={selectedShipmentIds.includes(shipment._id)}
                      onChange={() => toggleShipment(shipment._id)}
                    />
                  </TableCell>
                  <TableCell>{shipment.shipmentNumber}</TableCell>
                  <TableCell>
                    {deliveryDate ? formatDateTime(deliveryDate) : "N/A"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid={testIds.invoiceSeparationModalCancel}
          onClick={onClose}
          disabled={isLoading}
        >
          {t("common:cancel", "Cancel")}
        </Button>
        <LoadingButton
          data-testid={testIds.invoiceSeparationModalSeparate}
          variant="contained"
          loading={isLoading}
          disabled={!selectedShipmentIds.length}
          onClick={() => onSubmit(selectedShipmentIds)}
        >
          {t("separate", "Separate")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceSeparationModal;
